import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import { focusLockInstanceDecorator } from '@bingads-webui/focus-lock-decorator';
import { getTabbableNodes } from '@bingads-webui-react/focus-lock';
import { getPosition } from './get-position';

const editorClass = 'grid-popupeditor';

/**
* Provide grid popup editor function
*
* <br>It enabled functions:
*   render the editor view above or below,
*   close when editor view trigger close event,
*   close when usre click other part of document,
*
* <br>Triggered event close when it closes itself
*/
export class GridCustomizedPopupEditor extends Backbone.View {
  get attributes() {
    return {
      role: 'dialog',
    };
  }

  constructor(options) {
    // set default class name for popup-editor to be 'grid-popupeditor'
    super(_.extend({ className: editorClass }, options));
  }

  /**
   * Initilize view settings
   *
   * @param {Object} param - An object containing these default options for view:
   * @param {Object} param.editorView - editorView that will be host inside popup editor
   * @param {Object} param.editorPositionFactory - a factory to generate position of editor,
   * which overrides default editor position
   * @param {Object} param.cellPosition - position where popup editor event was triggered
   * @param {boolean} param.renderBelow - whether to render poup editor below cellPosition,
     default false
   * @param {boolean} param.CultureIsRTL - whether culture is RTL, default false
   * @param {boolean} param.noDismiss - whether to not close editor when click outside of it,
   *   default false (means close it by default)
   * @param {boolean} param.parentContainerSelector - custom parent container selector
   * (instead of body)
   * @param {boolean} adjustTopPosition - whether to adjust top position,
     default true
   * @returns {void} void
  */
  initialize({
    editorView,
    editorPositionFactory,
    cellPosition,
    renderBelow = false,
    CultureIsRTL = false,
    noDismiss = false,
    customDismissCondition = null,
    parentContainerSelector = null,
    lockRegionAriaLabel = '',
    adjustTopPosition = true,
  }) {
    this.editorView = editorView;
    this.editorPositionFactory = editorPositionFactory;
    this.cellPosition = cellPosition;
    this.renderBelow = renderBelow;
    this.CultureIsRTL = CultureIsRTL;
    this.parentContainerSelector = parentContainerSelector || 'body';
    this.hasCustomParentContainer = !!parentContainerSelector;
    this.adjustTopPosition = adjustTopPosition;

    this.dismiss = () => {
      this.trigger('close');
      this.remove();
    };

    const FocusLockEditorView = focusLockInstanceDecorator(this.editorView, {
      onExit: this.dismiss.bind(this),
      lockRegionAriaLabel,
      focusFirstItemOnMount: true,
    }, {
      managed: true,
    });

    this.focusLockEditorView = new FocusLockEditorView();

    this.clickToDismiss = (event) => {
      const defaultDismissCondition = e => $(e.target).closest(`.${this.className.replace(/ /g, '.')}`).length === 0;
      const dismissCondition = customDismissCondition || defaultDismissCondition;

      // Decide whether it is inside the popup editor
      if (dismissCondition(event)) {
        if (noDismiss) {
          return;
        }
        this.dismiss();
      }
    };
  }

  render() {
    this.$el.html(this.focusLockEditorView.render().el);

    this.editorView.on('close', (update) => {
      this.trigger('close', update);
      this.remove();
    });

    this.$el.hide();

    // position calculation need to be after the element appended to DOM, so defer it
    _.defer(() => {
      const position = _.isFunction(this.editorPositionFactory) ?
        this.editorPositionFactory(this.$el) :
        getPosition(
          this.cellPosition,
          this.$el,
          this.CultureIsRTL,
          this.renderBelow,
          this.hasCustomParentContainer && this.parentContainerSelector,
          this.adjustTopPosition
        );
      this.$el.css(position);
      this.$el.fadeIn();

      if (_.isFunction(this.editorView.setFocus)) {
        this.editorView.setFocus();
      } else if (this.editorView.el) {
        // filter the not visible element out which may due to parent display property
        const tabbableNode = _.filter(getTabbableNodes(this.editorView.el), item => $(item).is(':visible'));

        if (tabbableNode && tabbableNode.length > 0) {
          tabbableNode[0].focus({
            preventScroll: true,
          });
        }
      }
      // add data-focused flag to indicate the focus operation is done
      this.$el.attr('data-focused', '');

      $(window).on('click', this.clickToDismiss);
      $(window).on('hashchange', this.dismiss);
    });

    $(this.parentContainerSelector).append(this.el);

    return this;
  }

  remove() {
    this.focusLockEditorView.remove();
    this.editorView.remove();
    $(window).off('click', this.clickToDismiss);
    $(window).off('hashchange', this.dismiss);
    super.remove();
  }
}
